/* eslint perfectionist/sort-imports: off */
import 'reflect-metadata'
import type { AppConfig, Config, FeaturesConfig } from '@empathyco/playboard-core/dist/src'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import {
  APP_MESSAGES,
  AUDIT_MESSAGES,
  AUTH_MESSAGES,
  BROWSE_ANALYTICS_MESSAGES,
  CONTAINER,
  CRUD_MESSAGES,
  ERRORS_MESSAGES,
  EXPLAIN_MESSAGES,
  FeatureIdEnum,
  FEEDBACK_MESSAGES,
  FILTERS_MESSAGES,
  HIGHLIGHTS_MESSAGES,
  INSIGHTS_MESSAGES,
  INSTANCE_MANAGEMENT_MESSAGES,
  LocaleEnum,
  MOBILE_ALERT_MESSAGES,
  ROUTER_CONSTS,
  TOOLS_MESSAGES,
  URLUtils,
  USER_ROLE_MANAGEMENT_MESSAGES,
} from '@empathyco/playboard-core/dist/src'
import { init } from '@empathyco/playboard-core/dist/src/utils/init'
import { version } from '../package.json'
import App from './app.vue'
import { getRouter } from './router/router'
import { STORE } from './store/store'
;(async () => {
  let configFileUrl = `/config.json?version=${version}`
  const { VUE_APP_PREVIEW_PATH } = process.env
  if (VUE_APP_PREVIEW_PATH) {
    configFileUrl = `.${configFileUrl}`
  }
  const configFile = await fetch(configFileUrl)
  const config = (await configFile.json()) as Config

  window.chatWidget?.setConfig({
    conversationalApi: {
      baseUrl: config.chatWidgetServiceUrl,
      stream: true,
      history: true,
    },
    lang: 'en',
    floating: true,
  })

  const appConfig: AppConfig = {
    version,
    environment: config.environment,
    externalRoutes: config.externalRoutes,
    customization: config.customization,
    useFeedback: false,
    showEqualizeDisabledFeatures: config.environment === 'test',
    // TODO Fix Config service CORS issue on ephemeral environments
    loadInstancesFromInstanceManagement: !VUE_APP_PREVIEW_PATH,
    oidcLogin: !VUE_APP_PREVIEW_PATH,
    oidcLogout: config.environment === 'test',
  }

  const featuresConfig: FeaturesConfig = {
    [DomainEnum.SEARCH]: [
      {
        id: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.INSIGHTS][DomainEnum.SEARCH],
      },
      {
        id: FeatureIdEnum.OVERALL,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.SEARCH],
      },
      {
        id: FeatureIdEnum.QUERIES,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.QUERIES],
      },
      {
        id: FeatureIdEnum.SEMANTICS,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.SEMANTICS],
      },
      {
        id: FeatureIdEnum.NOW,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.NOW],
      },
      {
        id: FeatureIdEnum.ORIGINS,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.ORIGINS],
      },
      {
        id: FeatureIdEnum.KEYWORDS,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.KEYWORDS],
      },
      {
        id: FeatureIdEnum.CATEGORIES,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.CATEGORIES],
      },
      {
        id: FeatureIdEnum.CONVERSATIONAL,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.CONVERSATIONAL],
      },
      {
        id: FeatureIdEnum.RELATED_PROMPTS_ANALYTICS,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_PROMPTS_ANALYTICS],
      },
      {
        id: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.PLAY][DomainEnum.SEARCH],
      },
      {
        id: FeatureIdEnum.EQUALIZE,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.EQUALIZE].list,
      },
      {
        id: FeatureIdEnum.SYNONYM,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.SYNONYM].list,
      },
      {
        id: FeatureIdEnum.BLACKLIST,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.BLACKLIST].list,
      },
      {
        id: FeatureIdEnum.PROMOTION,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.PROMOTION].list,
      },
      {
        id: FeatureIdEnum.REDIRECTION,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.REDIRECTION].list,
      },
      {
        id: FeatureIdEnum.CONFIGURATION,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.SEARCH].list,
      },
      {
        id: FeatureIdEnum.RELATED_PROMPTS,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_PROMPTS].landing,
      },
      {
        id: FeatureIdEnum.RELATED_TAGS,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_TAGS].list,
      },
      {
        id: FeatureIdEnum.NEXT_QUERIES,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.NEXT_QUERIES].list,
      },
      {
        id: FeatureIdEnum.EXPLAIN,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.EXPLAIN].landing,
      },
    ],
    [DomainEnum.BROWSE]: [
      {
        id: FeatureIdEnum.INSIGHTS,
      },
      {
        id: FeatureIdEnum.OVERALL,
        parentId: FeatureIdEnum.INSIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.BROWSE],
      },
      {
        id: FeatureIdEnum.PLAY,
      },
      {
        id: FeatureIdEnum.CONFIGURATION,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE].root,
      },
    ],
    [DomainEnum.GLOBAL]: [
      {
        id: FeatureIdEnum.HIGHLIGHTS,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.HIGHLIGHTS],
      },
      {
        id: FeatureIdEnum.PLAY,
      },
      {
        id: FeatureIdEnum.FACETS,
        parentId: FeatureIdEnum.PLAY,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.FACETS].list,
      },
      {
        id: FeatureIdEnum.AUDIT,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.AUDIT],
      },
      {
        id: FeatureIdEnum.INSTANCE_MANAGEMENT,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.INSTANCE_MANAGEMENT].root,
        menuConfig: {
          displayOnHeader: true,
        },
      },
      {
        id: FeatureIdEnum.USER_ROLE_MANAGEMENT,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.USER_ROLE_MANAGEMENT],
        menuConfig: {
          displayOnHeader: true,
        },
      },
      {
        id: FeatureIdEnum.ACCOUNT,
        routeName: ROUTER_CONSTS.routes[FeatureIdEnum.ACCOUNT],
        menuConfig: {
          hidden: true,
        },
      },
    ],
  }

  const router = getRouter(appConfig)

  const messages: { [locale: string]: { [module: string]: unknown } } = {
    [LocaleEnum.ENGLISH]: {
      ...AUTH_MESSAGES[LocaleEnum.ENGLISH],
      ...FEEDBACK_MESSAGES[LocaleEnum.ENGLISH],
      ...FILTERS_MESSAGES[LocaleEnum.ENGLISH],
      ...HIGHLIGHTS_MESSAGES[LocaleEnum.ENGLISH],
      ...BROWSE_ANALYTICS_MESSAGES[LocaleEnum.ENGLISH],
      ...INSIGHTS_MESSAGES[LocaleEnum.ENGLISH],
      ...CRUD_MESSAGES[LocaleEnum.ENGLISH],
      ...{
        ...TOOLS_MESSAGES[LocaleEnum.ENGLISH],
        tools: {
          ...TOOLS_MESSAGES[LocaleEnum.ENGLISH].tools,
          configurations: {
            ...TOOLS_MESSAGES[LocaleEnum.ENGLISH].tools.configurations,
            enable: {
              confirmTitle: 'Turn on the results ranking config',
              confirmMessage:
                'This action will turn off the configurations for the selected queries/attribute values that already have one on, do you want to continue?',
              success: 'Results ranking configuration turned on successfully',
            },
          },
        },
      },
      ...ERRORS_MESSAGES[LocaleEnum.ENGLISH],
      ...EXPLAIN_MESSAGES[LocaleEnum.ENGLISH],
      ...MOBILE_ALERT_MESSAGES[LocaleEnum.ENGLISH],
      ...AUDIT_MESSAGES[LocaleEnum.ENGLISH],
      ...INSTANCE_MANAGEMENT_MESSAGES[LocaleEnum.ENGLISH],
      ...{
        ...APP_MESSAGES[LocaleEnum.ENGLISH],
        app: {
          ...APP_MESSAGES[LocaleEnum.ENGLISH].app,
          features: {
            ...APP_MESSAGES[LocaleEnum.ENGLISH].app.features,
            [FeatureIdEnum.CONFIGURATION]: {
              [DomainEnum.SEARCH]: 'Results Ranking',
              [DomainEnum.BROWSE]: 'Results Ranking',
            },
          },
          routes: {
            ...APP_MESSAGES[LocaleEnum.ENGLISH].app.routes,
            createConfiguration: {
              [DomainEnum.SEARCH]: 'New results ranking config',
              [DomainEnum.BROWSE]: 'New results ranking config',
            },
            updateConfiguration: {
              [DomainEnum.SEARCH]: 'Update results ranking config',
              [DomainEnum.BROWSE]: 'Update results ranking config',
            },
          },
        },
      },
      ...USER_ROLE_MANAGEMENT_MESSAGES[LocaleEnum.ENGLISH],
    },
    [LocaleEnum.SPANISH]: {
      ...AUTH_MESSAGES[LocaleEnum.SPANISH],
      ...FEEDBACK_MESSAGES[LocaleEnum.SPANISH],
      ...FILTERS_MESSAGES[LocaleEnum.SPANISH],
      ...HIGHLIGHTS_MESSAGES[LocaleEnum.SPANISH],
      ...BROWSE_ANALYTICS_MESSAGES[LocaleEnum.SPANISH],
      ...INSIGHTS_MESSAGES[LocaleEnum.SPANISH],
      ...CRUD_MESSAGES[LocaleEnum.SPANISH],
      ...{
        ...TOOLS_MESSAGES[LocaleEnum.SPANISH],
        tools: {
          ...TOOLS_MESSAGES[LocaleEnum.SPANISH].tools,
          configurations: {
            ...TOOLS_MESSAGES[LocaleEnum.SPANISH].tools.configurations,
            enable: {
              confirmTitle: 'Activar Ranking de Resultados',
              confirmMessage:
                'Esta acción desactivará las configuraciones para el Result Ranking seleccionado, ¿desea continuar?',
              success: 'Result Ranking configurado correctamente',
            },
          },
        },
      },
      ...ERRORS_MESSAGES[LocaleEnum.SPANISH],
      ...EXPLAIN_MESSAGES[LocaleEnum.SPANISH],
      ...MOBILE_ALERT_MESSAGES[LocaleEnum.SPANISH],
      ...AUDIT_MESSAGES[LocaleEnum.SPANISH],
      ...INSTANCE_MANAGEMENT_MESSAGES[LocaleEnum.SPANISH],
      ...{
        ...APP_MESSAGES[LocaleEnum.SPANISH],
        app: {
          ...APP_MESSAGES[LocaleEnum.SPANISH].app,
          features: {
            ...APP_MESSAGES[LocaleEnum.SPANISH].app.features,
            [FeatureIdEnum.CONFIGURATION]: {
              [DomainEnum.SEARCH]: 'Ranking de resultados',
              [DomainEnum.BROWSE]: 'Ranking de resultados',
            },
          },
          routes: {
            ...APP_MESSAGES[LocaleEnum.SPANISH].app.routes,
            createConfiguration: {
              [DomainEnum.SEARCH]: 'Nuevo ranking de resultados',
              [DomainEnum.BROWSE]: 'Nuevo ranking de resultados',
            },
            updateConfiguration: {
              [DomainEnum.SEARCH]: 'Actualizar ranking de resultados',
              [DomainEnum.BROWSE]: 'Actualizar ranking de resultados',
            },
          },
        },
      },
      ...USER_ROLE_MANAGEMENT_MESSAGES[LocaleEnum.SPANISH],
    },
  }

  const app = init({
    config: {
      app: appConfig,
      services: config.services,
      features: featuresConfig,
      x: {
        endpoints: {
          search: `${URLUtils.extractServiceUrl(config.services.search)}/query/{extraParams.instanceId}/{extraParams.endpointId}`,
        },
      },
    },
    container: CONTAINER,
    store: STORE,
    router,
    messages,
    rootComponent: App,
  })
  app.$mount('#app')
})()
